import * as React from "react";
import { SuccessState, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

export function SuccessStateDemo() {
  // Interactive surface: a fake async "verify" that flips to the done state on
  // resolve, mirroring how a reset-password / verify-email screen swaps in
  // SuccessState once the request lands. `key` remounts it each run so the
  // entrance pop + confetti replay on every click.
  const [done, setDone] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [run, setRun] = React.useState(0);

  const verify = () => {
    setPending(true);
    setDone(false);
    // Stand-in for the network round-trip; resolves after a tick.
    window.setTimeout(() => {
      setPending(false);
      setDone(true);
      setRun((n) => n + 1);
    }, 600);
  };

  return (
    <Section
      id="success-state"
      title="SuccessState"
      description="The 'you did it' confirmation surface (reset done, email verified, invite accepted). Third member of the EmptyState / ErrorState set: same prop names and wrapper skeleton, so a screen swaps ErrorState to SuccessState by changing the tag. role=status / aria-live=polite, the action slot stays router-free."
    >
      <VariantBlock label="Default" caption="AIKO-voice copy, scale-in medallion, looped halo, no action">
        <div className="w-96">
          <SuccessState />
        </div>
      </VariantBlock>

      <VariantBlock label="With action" caption="reset-password done state; the CTA is a passed-in Button">
        <div className="w-96">
          <SuccessState
            title="Password reset successful."
            description="You can now log in with your new password."
            action={<Button>Go to sign in</Button>}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Static" caption="animate={false} paints the final state with no entrance, scale-in, or halo loop">
        <div className="w-96">
          <SuccessState
            animate={false}
            title="Email verified."
            description="You're good to go."
            action={<Button variant="outline">Continue</Button>}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="Interactive" caption="run the fake verify to mount the success state with opt-in confetti">
        <div className="w-96">
          {done ? (
            <SuccessState
              key={run}
              title="You're in."
              description="Your workspace is ready."
              action={
                <Button variant="outline" onClick={() => setDone(false)}>
                  Reset demo
                </Button>
              }
              celebrate
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-card px-6 py-12 text-center">
              <p className="text-sm text-muted-foreground">
                {pending ? "Verifying..." : "Confirm to see the success state."}
              </p>
              <Button onClick={verify} isLoading={pending} disabled={pending}>
                Verify
              </Button>
            </div>
          )}
        </div>
      </VariantBlock>
    </Section>
  );
}
